import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Radio, MapPin, FileDown, Users, HelpCircle, CheckCircle2, Circle, AlertTriangle, Search } from "lucide-react";
import Seo from "@/components/Seo";
import { GuideCard } from "@/components/GuideCard";
import { SeverityChip } from "@/components/Badges";
import { UkAlertMap } from "@/components/UkAlertMap";
import { FeaturedChecklistCard } from "@/components/twoweek/FeaturedChecklistCard";
import { getJSON } from "@/lib/api";
import { store } from "@/lib/store";
import { haversineKm } from "@/lib/geo";
import { useApp } from "@/context/AppContext";

const FIVE = [
  { id: "contacts", key: "five.contacts", to: "/my-plan" },
  { id: "power", key: "five.power", to: "/local" },
  { id: "warnings", key: "five.warnings", to: "/live" },
  { id: "meeting", key: "five.meeting", to: "/my-plan" },
  { id: "kit", key: "five.kit", to: "/guides/emergency-kit" },
];

export default function Home() {
  const [status, setStatus] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [five, setFive] = useState(store.getFive());
  const [highAlerts, setHighAlerts] = useState(null);
  const [filter, setFilter] = useState("all");
  const [severeOnly, setSevereOnly] = useState(false);
  const [pcQuery, setPcQuery] = useState(store.getSavedArea()?.postcode || "");
  const [pcLoading, setPcLoading] = useState(false);
  const [pcError, setPcError] = useState(false);
  const navigate = useNavigate();
  const [lastLookup, setLastLookup] = useState(null);
  const [savedArea, setSavedArea] = useState(store.getSavedArea());
  const [nearMe, setNearMe] = useState(false);
  const [watch, setWatch] = useState(store.getWatch());
  const [watchDenied, setWatchDenied] = useState(false);
  const { t } = useApp();

  useEffect(() => {
    getJSON("/status").then(setStatus).catch(() => {});
    getJSON("/guides", { featured: true }).then((d) => setFeatured(d.guides)).catch(() => {});
    getJSON("/live").then((d) => setHighAlerts((d.items || []).filter((i) => i.severity === "severe" || i.severity === "warning"))).catch(() => setHighAlerts([]));
  }, []);

  const findNearby = async (e) => {
    e.preventDefault();
    const pc = pcQuery.trim();
    if (!pc) return;
    setPcError(false); setPcLoading(true);
    try {
      const d = await getJSON(`/postcode/${encodeURIComponent(pc)}`);
      const loc = { postcode: d.postcode, lat: d.lat, lon: d.lon, area: d.area || d.postcode };
      setLastLookup(loc);
    } catch {
      setPcError(true);
    } finally {
      setPcLoading(false);
    }
  };

  const saveMyArea = () => { if (lastLookup) { store.saveSavedArea(lastLookup); setSavedArea(lastLookup); } };
  const removeMyArea = () => { store.clearSavedArea(); store.clearWatch(); setSavedArea(null); setWatch(null); setNearMe(false); };
  const openSavedEssentials = () => { if (savedArea) navigate(`/local?pc=${encodeURIComponent(savedArea.postcode)}`); };

  const toggleWatch = async () => {
    if (watch?.enabled) { store.clearWatch(); setWatch(null); return; }
    if (typeof Notification === "undefined") { setWatchDenied(true); return; }
    let perm = Notification.permission;
    if (perm !== "granted") { try { perm = await Notification.requestPermission(); } catch { perm = "denied"; } }
    if (perm !== "granted") { setWatchDenied(true); return; }
    setWatchDenied(false);
    const w = { enabled: true, primed: false, postcode: savedArea.postcode, lat: savedArea.lat, lon: savedArea.lon };
    store.saveWatch(w); store.saveWatchSeen([]); setWatch(w);
  };

  const base = highAlerts || [];
  const cats = [...new Set(base.map((i) => i.category))];
  const countByCat = base.reduce((acc, i) => { acc[i.category] = (acc[i.category] || 0) + 1; return acc; }, {});
  let filteredAlerts = filter === "all" ? base : base.filter((i) => i.category === filter);
  if (severeOnly) filteredAlerts = filteredAlerts.filter((i) => i.severity === "severe");
  if (nearMe && savedArea) {
    filteredAlerts = [...filteredAlerts]
      .map((it) => ({ ...it, _dist: it.lat != null ? haversineKm(savedArea.lat, savedArea.lon, it.lat, it.lon) : Infinity }))
      .sort((a, b) => a._dist - b._dist);
  }
  const alreadySaved = savedArea && lastLookup && savedArea.postcode === lastLookup.postcode;

  const toggleFive = (id) => { const n = { ...five, [id]: !five[id] }; setFive(n); store.saveFive(n); };
  const done = FIVE.filter((f) => five[f.id]).length;

  return (
    <div>
      <Seo title="Would you know what to do?" description="Friendly, practical UK guidance before, during and after an emergency. We'll help you get your household ready, check live official information and build a plan that fits your family." path="/" />

      {/* HERO */}
      <section className="bg-navy text-white">
        <div className="container-x py-14 sm:py-20">
          <div className="max-w-3xl animate-fade-up">
            <div className="label-caps text-blue-300 mb-4">{t("hero.eyebrow")}</div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">{t("hero.title")}</h1>
            <p className="mt-5 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl">{t("hero.subtitle")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/prepare" data-testid="cta-prepare" className="touch justify-center gap-2 rounded-lg bg-white px-6 py-4 text-base font-bold text-navy hover:bg-slate-100 transition-colors">
                {t("hero.cta.prepare")} <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/emergency" data-testid="cta-emergency" className="touch justify-center gap-2 rounded-lg bg-amber-500 px-6 py-4 text-base font-bold text-navy hover:bg-amber-400 border-2 border-amber-500 transition-colors">
                {t("hero.cta.emergency")} <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/live" data-testid="cta-live" className="touch justify-center gap-2 rounded-lg border-2 border-white/40 bg-white/5 px-6 py-4 text-base font-bold text-white hover:bg-white/10 transition-colors">
                <Radio className="h-5 w-5" /> {t("hero.cta.live")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED EVERGREEN GUIDE (below emergency banner + main emergency navigation) */}
      <section className="container-x pt-8 pb-2" aria-label="Featured guide">
        <FeaturedChecklistCard testid="home-featured-two-week" />
      </section>

      {/* UK ALERT MAP */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="container-x py-12">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="label-caps text-slate-500">{t("map.eyebrow")}</div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy">{t("map.title")}</h2>
              <p className="text-slate-600 mt-1 max-w-xl">{t("map.subtitle")}</p>
            </div>
            <form onSubmit={findNearby} data-testid="map-postcode-form" className="shrink-0">
              <label className="label-caps text-slate-500 block mb-1">{t("map.search.title")}</label>
              <div className="flex gap-2">
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input data-testid="map-postcode-input" value={pcQuery} onChange={(e) => setPcQuery(e.target.value)}
                    placeholder={t("map.search.placeholder")} aria-label={t("map.search.placeholder")}
                    className="w-44 rounded-md border border-slate-300 bg-white pl-8 pr-3 py-2.5 text-base text-navy" />
                </div>
                <button data-testid="map-postcode-submit" type="submit" disabled={pcLoading}
                  className="touch gap-1.5 rounded-md bg-navy px-4 text-sm font-bold text-white hover:bg-navy-800 disabled:opacity-60">
                  <Search className="h-4 w-4" /> {pcLoading ? "…" : t("map.search.button")}
                </button>
              </div>
              {pcError && <p data-testid="map-postcode-error" className="text-xs text-amber-700 mt-1 max-w-[16rem]">{t("map.search.error")}</p>}
              {lastLookup && (
                <Link to={`/local?pc=${encodeURIComponent(lastLookup.postcode)}`} data-testid="map-postcode-local-link" className="block text-xs font-semibold text-royal hover:underline mt-1.5">{t("map.search.open")} {lastLookup.postcode} →</Link>
              )}
              {lastLookup && !alreadySaved && (
                <button type="button" data-testid="save-area-btn" onClick={saveMyArea} className="text-xs font-semibold text-royal hover:underline mt-1.5 inline-block">★ {t("map.save")}</button>
              )}
              {alreadySaved && <span data-testid="area-saved-note" className="text-xs font-semibold text-green-700 mt-1.5 inline-block">{t("map.saved.done")}</span>}
            </form>
          </div>

          {savedArea && (
            <div data-testid="saved-area-card" className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-navy"><MapPin className="h-4 w-4 text-royal" /><span className="label-caps text-slate-500">{t("map.saved.label")}</span><span className="font-mono font-bold">{savedArea.postcode}</span></div>
                <div className="flex flex-wrap items-center gap-2 ml-auto">
                  <button data-testid="near-me-toggle" onClick={() => setNearMe((v) => !v)} aria-pressed={nearMe}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${nearMe ? "bg-royal text-white border-royal" : "bg-white text-navy border-slate-300 hover:border-navy"}`}>{t("map.nearme")}</button>
                  <button data-testid="watch-area-toggle" onClick={toggleWatch} aria-pressed={!!watch?.enabled}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${watch?.enabled ? "bg-green-600 text-white border-green-600" : "bg-white text-navy border-slate-300 hover:border-navy"}`}>
                    <Radio className="h-3.5 w-3.5" /> {watch?.enabled ? t("map.watch.on") : t("map.watch.off")}
                  </button>
                  <button data-testid="saved-essentials-btn" onClick={openSavedEssentials} className="rounded-md bg-navy px-3 py-1.5 text-xs font-bold text-white hover:bg-navy-800">{t("map.saved.essentials")}</button>
                  <button data-testid="saved-remove-btn" onClick={removeMyArea} className="text-xs font-semibold text-slate-500 hover:text-navy underline">{t("map.saved.remove")}</button>
                </div>
              </div>
              {watchDenied && <p data-testid="watch-denied" className="text-xs text-amber-700 mt-2">{t("map.watch.denied")}</p>}
            </div>
          )}

          {highAlerts && highAlerts.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="map-filters">
              <span className="label-caps text-slate-500 mr-1">{t("map.filter.label")}</span>
              <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label={t("map.filter.all")} count={base.length} tid="all" />
              {cats.map((c) => <FilterChip key={c} active={filter === c} onClick={() => setFilter(c)} label={t(`cat.${c}`)} count={countByCat[c]} tid={c} />)}
              <button data-testid="severe-only-toggle" onClick={() => setSevereOnly((v) => !v)} aria-pressed={severeOnly}
                className={`ml-1 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${severeOnly ? "bg-red-600 text-white border-red-600" : "bg-white text-red-700 border-red-300 hover:border-red-500"}`}>
                {t("map.severe.toggle")}
              </button>
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-2 items-start">
            <div className="rounded-xl border border-slate-200 bg-white p-4 flex justify-center">
              <div className="w-full max-w-xs">
                <UkAlertMap items={filteredAlerts} />
                <div className="mt-3 flex items-center justify-center gap-4 text-xs text-slate-600">
                  <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#dc2626" }} /> {t("map.severe")}</span>
                  <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#f59e0b" }} /> {t("map.warning")}</span>
                </div>
              </div>
            </div>
            <div>
              {highAlerts === null && <div className="h-40 rounded-lg border border-slate-200 bg-white animate-pulse" />}
              {highAlerts && highAlerts.length === 0 && (
                <div data-testid="no-high-alerts" className="rounded-xl border border-green-200 bg-green-50 p-6">
                  <div className="flex items-center gap-2 font-heading font-bold text-green-800"><CheckCircle2 className="h-5 w-5" /> {t("map.none.title")}</div>
                  <p className="text-sm text-green-900/80 mt-1">{t("map.none.body")}</p>
                  <Link to="/prepare" className="mt-3 inline-flex items-center gap-1 font-semibold text-green-800 hover:underline">{t("map.none.cta")} <ArrowRight className="h-4 w-4" /></Link>
                </div>
              )}
              {highAlerts && highAlerts.length > 0 && filteredAlerts.length === 0 && (
                <div data-testid="no-severe-alerts" className="rounded-xl border border-green-200 bg-green-50 p-6">
                  <div className="flex items-center gap-2 font-heading font-bold text-green-800"><CheckCircle2 className="h-5 w-5" /> {t("map.none.severe")}</div>
                </div>
              )}
              {highAlerts && highAlerts.length > 0 && filteredAlerts.length > 0 && (
                <ul className="space-y-2" data-testid="high-alerts-list">
                  {filteredAlerts.slice(0, 6).map((it, i) => (
                    <li key={(it.feed_key || "a") + i}>
                      <Link to="/live" data-testid={`high-alert-${i}`} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:border-navy transition-colors">
                        <AlertTriangle className={`h-5 w-5 shrink-0 mt-0.5 ${it.severity === "severe" ? "text-red-600" : "text-amber-600"}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2"><SeverityChip level={it.severity} /><span className="label-caps text-slate-500 truncate">{it.category}</span>{typeof it._dist === "number" && isFinite(it._dist) && <span className="label-caps text-royal">{Math.round(it._dist)} km away</span>}</div>
                          <div className="font-heading font-semibold text-navy text-sm leading-tight mt-1 truncate">{it.title}</div>
                          <div className="text-xs text-slate-500 font-mono truncate">{it.area}</div>
                        </div>
                      </Link>
                    </li>
                  ))}
                  <li><Link to="/live" className="inline-flex items-center gap-1 text-sm font-semibold text-royal hover:underline mt-1">{t("map.seeall")} <ArrowRight className="h-4 w-4" /></Link></li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* LIVE STATUS STRIP */}
      <section className="border-b border-slate-200 bg-white">
        <div className="container-x py-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps text-slate-500">{t("strip.eyebrow")}</div>
              <h2 className="font-heading font-bold text-2xl text-navy">{t("strip.title")}</h2>
            </div>
            <Link to="/live" data-testid="live-link" className="text-sm font-semibold text-royal inline-flex items-center gap-1 hover:underline">{t("strip.viewall")} <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {status ? status.modules.slice(0, 10).map((m) => (
              <Link to="/live" key={m.category} data-testid={`status-tile-${m.category.split(" ")[0].toLowerCase()}`} className="rounded-lg border border-slate-200 bg-white p-3 hover:border-navy transition-colors">
                <div className="text-xs font-semibold text-navy leading-tight min-h-[32px]">{m.category}</div>
                <div className="mt-2">{m.available ? <SeverityChip level={m.top_severity} /> : <span className="label-caps text-amber-700">Unavailable</span>}</div>
              </Link>
            )) : Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-20 rounded-lg border border-slate-200 bg-slate-50 animate-pulse" />)}
          </div>
          <p className="mt-3 text-xs font-mono text-slate-500">{t("strip.english")}{status?.updated_at ? ` · ${new Date(status.updated_at).toLocaleTimeString("en-GB")}` : ""}</p>
        </div>
      </section>

      {/* FOUR MODES */}
      <section className="container-x py-12">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <ModeCard to="/prepare" tid="prepare" icon={ShieldCheck} title={t("mode.prepare.t")} text={t("mode.prepare.d")} />
          <ModeCard to="/live" tid="live" icon={Radio} title={t("mode.live.t")} text={t("mode.live.d")} />
          <ModeCard to="/my-plan" tid="my-plan" icon={FileDown} title={t("mode.myplan.t")} text={t("mode.myplan.d")} />
          <ModeCard to="/local" tid="local" icon={MapPin} title={t("mode.local.t")} text={t("mode.local.d")} />
        </div>
      </section>

      {/* FIVE THINGS TODAY */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="container-x py-12">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-6">
            <div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy">{t("five.title")}</h2>
              <p className="text-slate-600 mt-1">{t("five.subtitle")}</p>
            </div>
            <div data-testid="five-progress" className="rounded-full bg-navy text-white px-4 py-2 font-mono font-bold">{done}/5 {t("five.complete")}</div>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {FIVE.map((f) => (
              <li key={f.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-4">
                <button data-testid={`five-${f.id}`} onClick={() => toggleFive(f.id)} aria-pressed={!!five[f.id]} title="Mark as done" className="shrink-0">
                  {five[f.id] ? <CheckCircle2 className="h-6 w-6 text-green-600" /> : <Circle className="h-6 w-6 text-slate-300" />}
                </button>
                <span className={`text-sm font-medium flex-1 min-w-0 ${five[f.id] ? "line-through text-slate-400" : "text-navy"}`}>{t(f.key)}</span>
                <Link to={f.to} data-testid={`five-go-${f.id}`} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-royal px-3 py-1.5 text-xs font-bold text-white hover:bg-unionblue transition-colors">
                  {t("five.action")} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* WHAT WOULD YOU DO IF + MOST IMPORTANT GUIDES */}
      <section className="container-x py-12">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="rounded-xl bg-navy text-white p-6 sticky top-20">
              <HelpCircle className="h-8 w-8 text-blue-300" />
              <h2 className="font-heading font-bold text-2xl mt-3">{t("wwyd.title")}</h2>
              <p className="text-slate-300 mt-2 text-sm">{t("wwyd.body")}</p>
              <Link to="/emergency" data-testid="what-would-you-do" className="mt-4 touch justify-center gap-2 rounded-lg bg-amber-500 px-5 py-3 font-bold text-navy w-full">{t("wwyd.cta")} <ArrowRight className="h-5 w-5" /></Link>
            </div>
          </div>
          <div className="lg:col-span-2">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">{t("guides.title")}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {featured.slice(0, 6).map((g) => <GuideCard key={g.slug} guide={g} />)}
            </div>
          </div>
        </div>
      </section>

      {/* HANDBOOK + COMMUNITY */}
      <section className="container-x pb-16 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 flex flex-col">
          <FileDown className="h-8 w-8 text-royal" />
          <h3 className="font-heading font-bold text-xl text-navy mt-3">{t("handbook.title")}</h3>
          <p className="text-slate-600 mt-1 text-sm flex-1">{t("handbook.body")}</p>
          <Link to="/downloads" className="mt-4 inline-flex items-center gap-1 font-semibold text-royal">{t("handbook.cta")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 flex flex-col">
          <Users className="h-8 w-8 text-royal" />
          <h3 className="font-heading font-bold text-xl text-navy mt-3">{t("community.title")}</h3>
          <p className="text-slate-600 mt-1 text-sm flex-1">{t("community.body")}</p>
          <div className="mt-4 flex gap-4"><Link to="/community" className="font-semibold text-royal">{t("community.a")}</Link><Link to="/business" className="font-semibold text-royal">{t("community.b")}</Link></div>
        </div>
      </section>

    </div>
  );
}

function FilterChip({ active, onClick, label, count, tid }) {
  const slug = String(tid).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <button type="button" onClick={onClick} data-testid={`map-filter-${slug}`} aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${active ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-300 hover:border-navy"}`}>
      {label}
      {typeof count === "number" && (
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-mono leading-none ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>{count}</span>
      )}
    </button>
  );
}

function ModeCard({ to, tid, icon: Icon, title, text }) {
  return (
    <Link to={to} data-testid={`mode-${tid}`} className="group rounded-xl border border-slate-200 bg-white p-6 hover:border-navy hover:shadow-md transition-all">
      <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-navy group-hover:bg-navy group-hover:text-white transition-colors"><Icon className="h-6 w-6" /></span>
      <h3 className="font-heading font-bold text-xl text-navy mt-4">{title}</h3>
      <p className="text-slate-600 text-sm mt-1">{text}</p>
    </Link>
  );
}
